/**
 * Client-side photo/video optimization for explore drops (before Storage upload).
 */

import { VIDEO_MAX_BYTES, VIDEO_MAX_SECONDS } from './exploreDrops';

export const PHOTO_MAX_EDGE_PX = 1600;
export const PHOTO_JPEG_QUALITY = 0.82;
export const THUMB_MAX_EDGE_PX = 480;
export const THUMB_JPEG_QUALITY = 0.7;
export const VIDEO_MAX_EDGE_PX = 720;
export const VIDEO_TARGET_BITS_PER_SECOND = 1_200_000;

/** Scale dimensions so the longest edge is at most maxEdge (preserving aspect). */
export function fitWithinMaxEdge(width, height, maxEdge) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) return { width: 0, height: 0 };
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return { width: Math.round(w), height: Math.round(h) };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

function blobToFile(blob, name, type) {
  return new File([blob], name, { type: type || blob.type || 'application/octet-stream' });
}

/**
 * Resize + JPEG-compress an image File/Blob.
 * @returns {Promise<File>}
 */
export async function compressExplorePhoto(file, {
  maxEdge = PHOTO_MAX_EDGE_PX,
  quality = PHOTO_JPEG_QUALITY,
} = {}) {
  if (!file) throw new Error('Choose a photo.');
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = fitWithinMaxEdge(bitmap.width, bitmap.height, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare photo.');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Could not compress photo.'))),
        'image/jpeg',
        quality
      );
    });
    return blobToFile(blob, 'photo.jpg', 'image/jpeg');
  } finally {
    bitmap.close?.();
  }
}

/**
 * Read duration (seconds) from a video File/Blob.
 * WebM from MediaRecorder often reports Infinity until seeked — try that before failing.
 * @returns {Promise<number>}
 */
export function readVideoDurationSeconds(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    let settled = false;
    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
    };

    const finish = (duration) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error('Could not read video length.'));
        return;
      }
      resolve(duration);
    };

    const fail = (message) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };

    video.onerror = () => fail('Could not read video.');

    video.onloadedmetadata = () => {
      const duration = Number(video.duration);
      if (Number.isFinite(duration) && duration > 0) {
        finish(duration);
        return;
      }
      // Chrome WebM: seek to force duration calculation
      video.onseeked = () => {
        finish(Number(video.duration));
      };
      try {
        video.currentTime = 1e101;
      } catch {
        fail('Could not read video length.');
      }
    };

    video.src = url;
    video.load();
  });
}

/** Pure duration gate used by assertVideoWithinMaxSeconds. */
export function isVideoDurationAllowed(duration, maxSeconds = VIDEO_MAX_SECONDS) {
  return Number.isFinite(duration) && duration > 0 && duration <= maxSeconds + 0.15;
}

export async function assertVideoWithinMaxSeconds(file, maxSeconds = VIDEO_MAX_SECONDS) {
  const duration = await readVideoDurationSeconds(file);
  if (!isVideoDurationAllowed(duration, maxSeconds)) {
    throw new Error(`Videos must be ${maxSeconds} seconds or shorter.`);
  }
  return duration;
}

function pickRecorderMimeType() {
  const candidates = [
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9,opus',
    'video/webm',
  ];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

/**
 * Re-encode a short video via canvas + MediaRecorder (~720p).
 * Falls back to the original file when re-encode is unavailable or fails,
 * if the original still fits size limits.
 */
export async function compressExploreVideo(file, {
  maxEdge = VIDEO_MAX_EDGE_PX,
  maxSeconds = VIDEO_MAX_SECONDS,
  bitsPerSecond = VIDEO_TARGET_BITS_PER_SECOND,
  skipDurationCheck = false,
} = {}) {
  if (!file) throw new Error('Choose a video.');
  if (!skipDurationCheck) {
    await assertVideoWithinMaxSeconds(file, maxSeconds);
  }

  const mimeType = pickRecorderMimeType();
  if (!mimeType || typeof MediaRecorder === 'undefined') {
    return keepOriginalIfUploadable(file);
  }

  try {
    return await reencodeVideoFile(file, { maxEdge, maxSeconds, bitsPerSecond, mimeType });
  } catch {
    return keepOriginalIfUploadable(file);
  }
}

function keepOriginalIfUploadable(file) {
  const type = file.type || '';
  if (file.size > VIDEO_MAX_BYTES) {
    throw new Error('Video must be under 25MB.');
  }
  if (type === 'video/mp4' || type === 'video/webm') {
    return file instanceof File ? file : blobToFile(file, type === 'video/webm' ? 'video.webm' : 'video.mp4', type);
  }
  throw new Error('Could not compress this video — try recording in the app.');
}

async function reencodeVideoFile(file, { maxEdge, maxSeconds, bitsPerSecond, mimeType }) {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';

  try {
    await new Promise((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('Could not load video.'));
      video.src = url;
      video.load();
    });

    const srcW = video.videoWidth || 640;
    const srcH = video.videoHeight || 360;
    const { width, height } = fitWithinMaxEdge(srcW, srcH, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare video.');

    const stream = canvas.captureStream(30);
    const audioTracks = video.captureStream?.()?.getAudioTracks?.() || [];
    audioTracks.forEach((track) => stream.addTrack(track));

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: bitsPerSecond,
    });
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data?.size) chunks.push(e.data);
    };

    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = () => resolve();
      recorder.onerror = () => reject(new Error('Video encode failed.'));
    });

    recorder.start(200);
    await video.play();

    const deadline = Math.min(
      Number.isFinite(video.duration) ? video.duration : maxSeconds,
      maxSeconds
    );

    await new Promise((resolve) => {
      const draw = () => {
        if (video.ended || video.paused || video.currentTime >= deadline) {
          resolve();
          return;
        }
        ctx.drawImage(video, 0, 0, width, height);
        requestAnimationFrame(draw);
      };
      draw();
      video.onended = () => resolve();
      window.setTimeout(() => {
        try {
          video.pause();
        } catch {
          /* ignore */
        }
        resolve();
      }, Math.ceil(deadline * 1000) + 250);
    });

    try {
      video.pause();
    } catch {
      /* ignore */
    }
    if (recorder.state !== 'inactive') recorder.stop();
    await stopped;
    stream.getTracks().forEach((t) => t.stop());

    const blob = new Blob(chunks, { type: mimeType.split(';')[0] });
    if (!blob.size) throw new Error('Video encode produced empty output.');
    return blobToFile(blob, 'video.webm', 'video/webm');
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

export async function optimizeExploreMedia(type, file, options = {}) {
  if (type === 'photo') return compressExplorePhoto(file);
  if (type === 'video') return compressExploreVideo(file, options);
  return null;
}

/**
 * Small JPEG for Collection / card previews (~480px).
 * Photos: resize from the upload file. Videos: first-frame poster when possible.
 * @returns {Promise<File|null>}
 */
export async function makeExploreMediaThumb(type, file) {
  if (!file) return null;
  try {
    if (type === 'photo') {
      return await compressExplorePhoto(file, {
        maxEdge: THUMB_MAX_EDGE_PX,
        quality: THUMB_JPEG_QUALITY,
      });
    }
    if (type === 'video') {
      return await captureVideoPosterThumb(file, {
        maxEdge: THUMB_MAX_EDGE_PX,
        quality: THUMB_JPEG_QUALITY,
      });
    }
  } catch {
    return null;
  }
  return null;
}

async function captureVideoPosterThumb(file, { maxEdge, quality }) {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';

  try {
    await new Promise((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('Could not load video for thumb.'));
      video.src = url;
      video.load();
    });

    const seekTo = Math.min(0.12, Number.isFinite(video.duration) ? video.duration * 0.05 : 0.12);
    if (Number.isFinite(seekTo) && seekTo > 0) {
      await new Promise((resolve) => {
        const done = () => {
          video.onseeked = null;
          resolve();
        };
        video.onseeked = done;
        try {
          video.currentTime = seekTo;
        } catch {
          done();
        }
        window.setTimeout(done, 400);
      });
    }

    const srcW = video.videoWidth || 0;
    const srcH = video.videoHeight || 0;
    if (srcW <= 0 || srcH <= 0) return null;

    const { width, height } = fitWithinMaxEdge(srcW, srcH, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, width, height);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Could not make video thumb.'))),
        'image/jpeg',
        quality
      );
    });
    return blobToFile(blob, 'thumb.jpg', 'image/jpeg');
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}
